import React, { useEffect, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { toast } from "../../components/ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

export default function OnboardingPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/lifecycle/onboarding/my-tasks`, { headers: authH() });
      const d = await r.json();
      setData(d);
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, []);

  const complete = async (taskId) => {
    setBusy(taskId);
    // Optimistically flip the checkbox so the UI feels instant. We snapshot
    // the prior state so we can revert if the POST fails.
    const prevData = data;
    setData(d => {
      if (!d || !Array.isArray(d.tasks)) return d;
      return {
        ...d,
        tasks: d.tasks.map(t =>
          t.task_id === taskId ? { ...t, completed: true } : t
        ),
        onboarding: d.onboarding
          ? { ...d.onboarding, completed: (d.onboarding.completed || 0) + 1 }
          : d.onboarding,
      };
    });
    try {
      const r = await fetch(`${API}/api/lifecycle/onboarding/tasks/${taskId}/complete`, {
        method: "POST", headers: authH(), body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Task completed ✓");
      await load();
    } catch (e) {
      // Revert optimistic state on failure
      setData(prevData);
      toast.error("Could not mark task complete");
    } finally { setBusy(null); }
  };

  const ob = data?.onboarding;
  const tasks = data?.tasks || [];
  const pct = ob ? Math.round((ob.completed / Math.max(1, ob.total)) * 100) : 0;

  return (
    <MessengerLayout>
      <div className="max-w-3xl mx-auto p-6 space-y-5">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold text-white">My Onboarding</h1>
          <p className="text-xs text-white/40">Welcome aboard — work through these tasks at your own pace.</p>
        </header>

        {error && <p className="text-sm text-red-400">⚠ {error}</p>}

        {!ob ? (
          <div className={`${card} p-6`}>
            <p className="text-white/60 text-sm">You don't have an active onboarding journey assigned.</p>
          </div>
        ) : (
          <>
            <div className={`${card} p-5 space-y-3`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#00ff90]/60 uppercase">Start date</p>
                  <p className="text-sm text-white/80">{ob.start_date || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-[#00ff90]/60 uppercase">Buddy</p>
                  <p className="text-sm text-white/80">{ob.buddy_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-[#00ff90]/60 uppercase">Progress</p>
                  <p className="text-sm font-medium text-[#00ff90]">{ob.completed} / {ob.total}</p>
                </div>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-[#00ff90] transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <ul className="space-y-2">
              {tasks.map(t => (
                <li key={t.task_id} className={`${card} p-4 flex items-start gap-3`}>
                  <input
                    type="checkbox" checked={t.completed} disabled={t.completed || busy === t.task_id}
                    onChange={() => complete(t.task_id)}
                    className="mt-1 w-4 h-4 accent-[#00ff90]"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm ${t.completed ? "text-white/40 line-through" : "text-white/85"}`}>{t.title}</p>
                      {t.is_required && <span className="text-xs px-1.5 py-0.5 rounded border border-yellow-500/30 bg-yellow-500/10 text-yellow-400">Required</span>}
                    </div>
                    {t.description && <p className="text-xs text-white/40 mt-1">{t.description}</p>}
                    <p className="text-xs text-white/30 mt-1">
                      Owner: <span className="capitalize">{t.assigned_to_role}</span>
                      {t.due_date && <> · Due: {t.due_date}</>}
                      {t.completed && t.completed_at && <> · Done: {new Date(t.completed_at).toLocaleDateString()}</>}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </MessengerLayout>
  );
}
